/**
 * PM Assistant Widget - Compact Agentic PM Intelligence for PM Center
 * 
 * Embeds Daily Brief, Recommendations, and Pending Proposals into PM Center
 * Part of R1-003: PM Center Integration
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Brain, AlertTriangle, CheckCircle, Loader2, Lightbulb,
  ChevronRight, Target, ThumbsUp, ThumbsDown, Zap
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuthHeaders = () => {
  const token = localStorage.getItem('simpliview_token');
  const tenantId =
    localStorage.getItem('impersonated_tenant_id') ||
    localStorage.getItem('simpliview_user_tenant_id') ||
    localStorage.getItem('simpliview_tenant_id');
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(tenantId ? { 'X-Tenant-ID': tenantId } : {})
  };
};

const getSeverityBadge = (severity) => {
  const colors = {
    critical: 'bg-red-100 text-red-800 border-red-300',
    high: 'bg-orange-100 text-orange-800 border-orange-300',
    medium: 'bg-amber-100 text-amber-800 border-amber-300',
    low: 'bg-blue-100 text-blue-800 border-blue-300'
  };
  return colors[severity] || 'bg-slate-100 text-slate-800 border-slate-300';
};

export default function PMAssistantWidget({ projectIds = [], compact = true }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [brief, setBrief] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [processingProposal, setProcessingProposal] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [briefRes, proposalsRes] = await Promise.all([
          axios.get(`${API_BASE}/pm-assistant/what-should-i-do-today`, { headers: getAuthHeaders() }),
          axios.get(`${API_BASE}/pm-assistant/proposals`, { headers: getAuthHeaders() })
        ]);
        setBrief(briefRes.data);
        setProposals(proposalsRes.data?.proposals || []);
      } catch (error) {
        console.error('Failed to load PM Assistant data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleApprove = async (proposalId) => {
    setProcessingProposal(proposalId);
    try {
      await axios.post(
        `${API_BASE}/pm-assistant/proposals/${proposalId}/approve`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Proposal approved');
      setProposals(prev => prev.filter(p => p.proposal_id !== proposalId));
    } catch (error) {
      toast.error('Failed to approve proposal');
    } finally {
      setProcessingProposal(null);
    }
  };

  const handleReject = async (proposalId) => {
    setProcessingProposal(proposalId);
    try {
      await axios.post(
        `${API_BASE}/pm-assistant/proposals/${proposalId}/reject`,
        { reason: 'Rejected from PM Center widget' },
        { headers: getAuthHeaders() }
      );
      toast.success('Proposal rejected');
      setProposals(prev => prev.filter(p => p.proposal_id !== proposalId));
    } catch (error) {
      toast.error('Failed to reject proposal');
    } finally {
      setProcessingProposal(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] shadow-[var(--shadow-card)] p-4">
        <div className="flex items-center justify-center gap-2 text-[var(--clr-text-muted)]">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading PM Assistant...</span>
        </div>
      </div>
    );
  }

  const criticalCount = brief?.critical_count || 0;
  const highCount = brief?.high_count || 0;
  const priorities = brief?.priorities || [];
  const overallStatus = brief?.overall_status || 'healthy';

  const statusColor = {
    critical: 'text-red-600 bg-red-50',
    attention_required: 'text-orange-600 bg-orange-50',
    monitoring: 'text-amber-600 bg-amber-50',
    healthy: 'text-emerald-600 bg-emerald-50'
  }[overallStatus] || 'text-slate-600 bg-slate-50';

  return (
    <div 
      className="bg-[var(--clr-surface-card)] rounded-lg border border-purple-200 shadow-[var(--shadow-card)] overflow-hidden"
      data-testid="pm-assistant-widget"
    >
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-3 py-2 flex items-center justify-between bg-gradient-to-r from-purple-50 to-violet-50 hover:from-purple-100 hover:to-violet-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Brain className="w-4 h-4 text-purple-600" />
          <span className="font-semibold text-[var(--clr-text-primary)] text-sm">PM Assistant</span>
          {/* Status Badge */}
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusColor}`}>
            {overallStatus.replace('_', ' ')}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {/* Quick Stats */}
          {criticalCount > 0 && (
            <span className="flex items-center gap-1 text-xs text-red-600">
              <AlertTriangle className="w-3 h-3" />
              {criticalCount} critical
            </span>
          )}
          {proposals.length > 0 && (
            <span className="flex items-center gap-1 text-xs text-purple-600">
              <Lightbulb className="w-3 h-3" />
              {proposals.length} pending
            </span>
          )}
          <ChevronRight className={`w-4 h-4 text-[var(--clr-text-muted)] transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </div>
      </button>

      {/* Expanded Content */}
      {expanded && (
        <div className="p-3 space-y-3">
          {/* What Should I Do Today Summary */}
          <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-4 h-4 text-purple-600" />
              <span className="text-sm font-medium text-[var(--clr-text-primary)]">Today&apos;s Focus</span>
            </div>
            {priorities.length > 0 ? (
              <div className="space-y-2">
                {priorities.slice(0, 3).map((item, idx) => (
                  <div 
                    key={idx}
                    className="flex items-start gap-2 p-2 bg-white rounded border border-[var(--clr-border-light)]"
                  >
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium border ${getSeverityBadge(item.severity)}`}>
                      {item.severity?.toUpperCase()}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-[var(--clr-text-primary)] truncate">{item.title}</p>
                      {item.recommendation && (
                        <p className="text-[10px] text-[var(--clr-text-muted)] mt-0.5 flex items-center gap-1">
                          <Lightbulb className="w-3 h-3 text-amber-500" />
                          {item.recommendation}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-2">
                <CheckCircle className="w-6 h-6 text-emerald-500 mx-auto mb-1" />
                <p className="text-xs text-[var(--clr-text-muted)]">No critical items requiring attention</p>
              </div>
            )}
          </div>

          {/* Pending Proposals */}
          {proposals.length > 0 && (
            <div className="bg-purple-50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-medium text-[var(--clr-text-primary)]">Pending Approvals</span>
              </div>
              <div className="space-y-2">
                {proposals.slice(0, 2).map((proposal) => (
                  <div 
                    key={proposal.proposal_id}
                    className="flex items-center justify-between p-2 bg-white rounded border border-purple-200"
                  >
                    <div className="flex-1 min-w-0 mr-2">
                      <p className="text-xs text-[var(--clr-text-primary)] truncate">{proposal.title}</p>
                      <p className="text-[10px] text-[var(--clr-text-muted)]">{proposal.type?.replace(/_/g, ' ')}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleReject(proposal.proposal_id)}
                        disabled={processingProposal === proposal.proposal_id}
                        className="p-1 text-red-500 hover:bg-red-50 rounded disabled:opacity-50"
                      >
                        <ThumbsDown className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => handleApprove(proposal.proposal_id)}
                        disabled={processingProposal === proposal.proposal_id}
                        className="p-1 text-emerald-500 hover:bg-emerald-50 rounded disabled:opacity-50"
                      >
                        {processingProposal === proposal.proposal_id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <ThumbsUp className="w-3 h-3" />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Link to Full Dashboard */}
          <button
            onClick={() => navigate('/ai/pm-assistant')}
            className="w-full text-center text-xs text-purple-600 hover:text-purple-800 flex items-center justify-center gap-1 py-1"
          >
            Open Full PM Assistant <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Collapsed Quick View */}
      {!expanded && (priorities.length > 0 || proposals.length > 0) && (
        <div className="px-3 py-2 border-t border-[var(--clr-border-light)]">
          <div className="flex items-center gap-2 text-xs text-[var(--clr-text-muted)]">
            {priorities.length > 0 && (
              <span className="truncate">{priorities[0]?.title}</span>
            )}
            <button
              onClick={() => navigate('/ai/pm-assistant')}
              className="ml-auto text-purple-600 hover:text-purple-800 flex items-center gap-0.5 flex-shrink-0"
            >
              View All <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
